import * as globals from '../globals';
import * as apiService from '../utils/apiService';
import { loadOptRunSetSelectionHelper } from '../utils/optRunHelper';
import { applyConditions, getPriceLevelLabels } from '../utils/dcmUtils'
import { getMeta } from '../utils/metaUtils'
import { guid } from "../utils/guid";


import { MAINHUB_MESSAGE_RECEIVED } from './action-types';
import { 
    OPTIMIZE_OPTRUN_RECEIVE,
    OPTIMIZE_OPTRUN_REQUEST,
    OPTIMIZE_OPTRUN_SCENARIO_RECEIVE,
    OPTIMIZE_OPTRUN_SCENARIO_REQUEST,
    STUDY_RESET,
    STUDY_RECEIVE,
    OPTIM_INIT_ATTS,
    OPTIM_SET_ATT_LOCKED,
    OPTIM_SET_LEVEL_ALLOWED,
    OPTIM_SET_MESSAGE_ALLOWED,
    OPTIM_SET_OPTIMIZATION_METRIC,
    OPTIM_REMOVE_OPTIMIZATION_SELECTION,
    OPTIM_TOGGLE_OPTIMIZATION_SELECTION,
    OPTIM_ADD_OPTIMIZATION_SELECTION,
    OPTIM_SET_RUNLIST,
    OPTIM_SET_RUNLIST_DATA,
    OPTIM_SET_RUNLIST_LOADING,
    OPTIM_UPDATE_RUNITEM,
    OPTIM_SET_LEVEL_COSTS,
    OPTIM_SET_METRIC_CONSTRAINTS
    // @ts-ignore
} from './action-types';
// @ts-ignore
import { actionCreators as configActionCreators } from './Config';
import { useSelector } from 'react-redux';

const isNull = (val: any, fallback: any) => (val === undefined || val === null) ? fallback : val


//export const researchScenariosActionType = 'RESEARCH_SCENARIOS_ACTION';

export interface OptRun {
    loading: boolean
    input: {
        goal: {
            targetMeasure: string
            targetFilter: string
            targetFilterLabel: string
            targetProducts: string[]
            targetCosts: object,
            metricConstraints: []
        }
    }
    output: {
        scenarios: any[]
    }
}

export interface Atts {
    name: string,    
    type: string,
    levels?: { value: number, label: string }[], // for discrete attributes
    min?: number, // for continuous attributes
    max?: number // for continuous attributes
}

export interface Products {
    uid: string,
    label: string,
    available: boolean,
    atts: Atts[]
}

export interface BrandEquities {
    level: string,
    data: { [key: string]: { [key: string]: number } }
}

export interface BrandEquitiesToOptimize {
    level: string,
    perceptions: { [key: string]: string[] }
}

export interface OptimState {
    studyId: string,
    uid: string,
    label: string,
    baseCaseId: string,
    goal: {
        targetMeasure: string,
        targetFilter: string,
        targetFilterLabel: string,
        targetProducts: {
            uid: string,
            label: string
        }[], 
        targetCosts: object,
        metricConstraints: []
    },
    config: {
        products: Products[],
        equity_drivers: BrandEquities,
        brand_equities_to_optimize: BrandEquitiesToOptimize
    },
    assumptions: { [key: string]: any },
    optimizerSettings: {
        algorithm: string,
        geneticAlgorithm: {
            population: number,
            seed: number,
            elitism: number,
            mutationRate: number
        }
    },
    defaultFilterLabel: string,
    optRunList: string[],
    optRunDict: { [key: string]: any },
    loading: boolean,
    error: any
}



const initialState: OptimState = {
    studyId: '',
    uid: '',
    label: '',
    baseCaseId: '',
    goal: {
        targetMeasure: '',
        targetFilter: '',
        targetFilterLabel: '',
        targetProducts: [], 
        targetCosts: {}
    },
    config: {
        products: [],
        equity_drivers: {
            level: '',
            data: {}
        },
        brand_equities_to_optimize: {
            level: '',
            perceptions: {}
        }
    },
    assumptions: {},
    optimizerSettings: {
        algorithm: 'genetic',
        geneticAlgorithm: {
            population: 100,
            seed: 123456789,
            elitism: 5,
            mutationRate: 0.01
        }
    },
    defaultFilterLabel: 'Total Population',
    optRunList: [],
    optRunDict: {},
    loading: false,
    error: null
}




export const getSavedScenarioKey = (uid: string) => `saved:${uid}`
export const getOptRunScenarioKey = (runId: string, index: any) => `optRun:${runId}:${index}`
export const getFeedScenarioKey = (postId: string) => `post:${postId}`


export const actionCreators = {

    loadOptRun: (runId: string, callback: any) => async (dispatch: any, getState: any) => {

        dispatch({ type: OPTIMIZE_OPTRUN_REQUEST, runId })

        const state = getState()

        try {
            const url = `${globals.apiRoot}/optimize/run/${runId}`;
            const response = await apiService.aGet(state.auth, url);
            if( response.ok ){
                const value = await response.json();
                dispatch({ type: OPTIMIZE_OPTRUN_RECEIVE, runId, value });

                if( callback ){
                    callback(value);
                }
            }
            else{
                dispatch({ type: OPTIMIZE_OPTRUN_RECEIVE, runId, error: response.statusText });
            }

        } catch (error) {
            console.error('loadOptRun error', error);
            dispatch({ type: OPTIMIZE_OPTRUN_RECEIVE, runId, error });
        }

    },

    loadOptRunScenario: (runId: string, scenarioKey: string, loadWhatIf: any, callback: any) => async (dispatch: any, getState: any) => {
        
        // for now, opening what-if and comparison

        dispatch({ type: OPTIMIZE_OPTRUN_SCENARIO_REQUEST, runId, key: scenarioKey });

        const state = getState();

        // get uid of study
        let studyId = state.study.uid;

        // get config
        // todo: separate play from base

        try {
            const response = await apiService.aGet(state.auth, `${globals.apiRoot}/study/${studyId}/optrun/${runId}/scenario/${scenarioKey}`);
            if( response.ok ){
                const data = await response.json();
            
                dispatch({ type: OPTIMIZE_OPTRUN_SCENARIO_RECEIVE, runId, key: scenarioKey, value: data });

                if( loadWhatIf ){

                    await loadOptRunSetSelectionHelper(data, state.study, (a: any, b: any) => {
                        configActionCreators.set(a, b)(dispatch, getState)
                    });

                    // hack?
                    dispatch({ type: `SCENARIO_ACTION:LOAD_SCENARIO_RESULT_SINGLECONFIG`, value: data });
                }

                if (callback) {
                    callback( data );
                }

            } 
            else {
                console.error(response);
                dispatch({ type: OPTIMIZE_OPTRUN_SCENARIO_RECEIVE, runId, key: scenarioKey, error: response.statusText });
            }

        } 
        catch (error) {
            console.error(error);
            dispatch({ type: OPTIMIZE_OPTRUN_SCENARIO_RECEIVE, runId, key: scenarioKey, error });
            
        }
        
    },

    initProdsToOptimize: (modelname: any) => async (dispatch: any, getState: any) => {
        let state = getState()

        const model = state.model[modelname]
        const prodsToOptimize = state.optimizer.config.products

        if (prodsToOptimize.length === 0) {
            dispatch({ type: OPTIM_INIT_ATTS, model })
        }   
    },


    // for optimizer. if att is locked, optimizer will leave it set at attValue
    setAttOptimizeable: (prodUid: any, attName: any, optimizeable: any, modelname: any) => async (dispatch: any, getState: any) => {
        let state = getState()

        const model = state.model[modelname]
        dispatch({ type: OPTIM_SET_ATT_LOCKED, prodUid, attName, optimizeable, model })
    },


	// for optimizer. if level is allowed, optimizer will consider it as possible value
	setLevelAllow: (prodUid: any, attName: any, levelValue: any, allowed: any) => async (dispatch: any) =>
		dispatch({
			type: OPTIM_SET_LEVEL_ALLOWED,
			prodUid,
			attName,
			levelValue,
			allowed,
		}),

	setMessageAllow: (key: any, name: any, allowed: any) => async (dispatch: any) =>
		dispatch({
			type: OPTIM_SET_MESSAGE_ALLOWED,
			key,
			name,
			allowed,
		}),

	setOptimizationMetric: (metric: any) => (dispatch: any) =>
		dispatch({ type: OPTIM_SET_OPTIMIZATION_METRIC, value: metric }),

	toggleOptimizationSelection: (prodsToOpt: any, callback: any) => (dispatch: any,	getState: any) => {
		dispatch({ type: OPTIM_TOGGLE_OPTIMIZATION_SELECTION, prodsToOpt })
		if (callback) {
			const state = getState()
			const newSelections = state.prodsToOpt
			callback(newSelections)
		}
	},

    addOptimizationSelection: (prod: any, callback: any) => (dispatch: any, getState: any) => {
        dispatch({ type: OPTIM_ADD_OPTIMIZATION_SELECTION, prod })
        
		if (callback) {
			const state = getState()
			const newSelections = state.prodsToOpt
			callback(newSelections)
		}
	},

	removeOptimizationSelection: (prod: any) => (dispatch: any,getState: any) => {
		dispatch({ type: OPTIM_REMOVE_OPTIMIZATION_SELECTION, prod })
		// if (callback) {
		// 	const state = getState()
		// 	const newSelections = state.prodsToOpt
		// 	callback(newSelections)
		// }
    },

    setFeatureCosts: (costs: object) => (dispatch: any,getState: any) => {
		dispatch({ type: OPTIM_SET_LEVEL_COSTS, costs })
		
    },

    setMetricConstraints: (constraints: []) => (dispatch: any,getState: any) => {
		dispatch({ type: OPTIM_SET_METRIC_CONSTRAINTS, constraints })
		
    },
    
    runOptimizer: (activeConfig:any) => async (dispatch: any, getState: any) => {

        let state: any = getState()
		let runId: any = guid()

		const auth: any = state.auth
		const study: any = state.study
        const optim: any = state.optimizer || {}
        const metric: any = optim.goal.targetMeasure || globals.default_optimizer_metric
        const costs: any = optim.goal.targetCosts
		const filterobj: any = state.filter.selectedFilter || globals.getDefaultFilter(state?.study)
		const model: any = state.model
        const config: any = model[activeConfig].config
        const modelInfo: any = JSON.parse(JSON.stringify(model[activeConfig].modelInfo))
        let attRules: any = null
        let attLookups: any = null
        let metricConstraints: any = optim.goal?.metricConstraints
        
        if (!config) return null

        const defAtts = config.productDef.atts


        const fetchMeta = async () => {
            // let optPayload = await getMeta(study.uid, auth, 'optimizerPayload')
            let m = await getMeta(study.uid, auth, model?.[activeConfig]?.modelInfo.attConditionsTable)
            if (m) {
                console.log('m', m);
                attRules = m
            }

            if (model?.[activeConfig]?.modelInfo.attConditionsLookups?.length > 0) {
                let lookups = await getMeta(study.uid, auth, model?.[activeConfig]?.modelInfo.attConditionsLookups)
            
                if (lookups) {
                    attLookups = lookups
                }
            }
        }

        await fetchMeta()

        let lookupFile: any = null
		const fetchLevelLabels = async () => {
            
            let lookup_file = await getMeta(study.uid, auth, model?.[activeConfig]?.modelInfo?.attConditionsLookups)
            if (lookup_file) {
                lookupFile = lookup_file
            }
        }

		await fetchLevelLabels()
		
		const levelLabels = await getPriceLevelLabels(config.play, lookupFile)
        
        
        //for FORD, need to change the totalmarketvolume from Ford tam assumptions page

        let assump = state.general?.custom?.ford_ass
		if (!assump) {
			assump = await getMeta(study.uid, auth, "fordAssumptionsV2")
		}	


        if (assump) {
            let year = modelInfo.name.match(/\d{4}$/)?.[0] || null
            let isPickup = modelInfo.name.toLowerCase().includes('truck')

            if(isPickup)
                modelInfo.totalMarketVolume = assump[`dcm_tam_consideration_${year}`]?.Pickup_Truck_Demand
            else
                modelInfo.totalMarketVolume = assump[`dcm_tam_consideration_${year}`]?.SUV_Segment_Demand
                
        }

        // console.log('modelInfo.totalMarketVolume', modelInfo.totalMarketVolume);
        
        const targetProducts: any = (optim.goal.targetProducts || [])
        // console.log('targetProducts', targetProducts)
        
        const products = await Promise.all(
            config.play.map(async (prod: any) => {
                    
					// const def = config.productDef
					let p: any = {
						uid: prod.uid,
						label: prod.label, 
						available: prod.available,
					}
					
                    const atts = await applyConditions(prod, defAtts, attRules?.rules, attLookups)
                    // console.log(`${prod.uid} atts`, atts);

                    atts.forEach((att: any) => {
                        
						// NOTE: it's possible that the start value doesn't exist in allowed values. figure out where and how that should be handled!!
						const notTargetSlot = !targetProducts.find((p1: any) => p1.uid == prod.uid)
						const isReadOnly = att.readOnly
						const attIsOptimizable = optim.config.products.find((f:any) => f.uid === prod.uid)?.atts.find((f:any) => f.name === att.name)

						// multiselect is a bit different than discrete or contin
						if (att.type == "multiselect") { //****************MUJLTISELECT ATTS HAVE OPTIONS??? */

							// loop through options
							att.options.forEach((option: any) => { //**FIX
								
								const currentVal = prod.play[option.name] || att.defaultValue

								if (notTargetSlot || isReadOnly || !attIsOptimizable) {
									p[option.name] = currentVal
								}
								else {
									p[option.name] = {
										startValue: currentVal,
										allowedLevels: [isNull(att.yesValue,1), isNull(att.noValue,0)]
									}
								}

							})

						}
						else { // DISCRETE OR CONTINUOUS ATTS
							
							let currentVal = prod[att.name] || att.defaultValue

							if (notTargetSlot || isReadOnly || !attIsOptimizable) {
								p[att.name] = currentVal
							}
							else {
								
                                if (att.type == "discrete") {
                                    const prods = optim.config.products.find((f: any) => f.uid === prod.uid)
                                    const p_att = prods?.atts.find((a:any) => a.name === att.name)
                                    const allowedLevels = p_att?.levels.filter((f: any) => f.allowed) 
                                    // console.log('allowedLevels', allowedLevels);
									p[att.name] = {
										startValue: currentVal,
										allowedLevels 
									}
								}
								else if (att.type == "contin") {
									p[att.name] = {
										startValue: currentVal,
										allowedRange: [att.min, att.max]
									}
								}
								
							}
						}						
					})
					return p
                })
        )


		// construct a label
		let label = "Maximize " + globals.metrics.find((m:any) => m.key.toLowerCase() === metric.toLowerCase())?.label + " of "
		if (targetProducts.length == 1) {
			label += `'${targetProducts[0]?.label}'` 
		}
		else {
			label += `'${targetProducts[0]?.label}'`
            for (let i = 1; i < targetProducts.length-1; i++){
				label += `, '${targetProducts[i]?.label}'`
			}
			label += ` and '${targetProducts[targetProducts.length - 1]?.label}'`
		}
		label += " for '" + filterobj?.label + "'"
        label += " started " + new Date().toLocaleString()       
        
        // console.log('attRules', attRules?.rules);
		// console.log('attLookups', attLookups);
		// console.log('costs', costs);

		var query: any = {
			studyId: state.study.uid,
            uid: runId,
            modelId: activeConfig,
            modelInfo,
            label,
            levelLabels,
			goal: {
				targetMeasure: metric,
				targetFilter: filterobj.syntax,
				targetFilterLabel: filterobj.label,
                targetProducts: targetProducts,
                metricConstraints
			},
			config: {
                products,
                levelcosts: costs,
                equity_drivers: {
                    //use brandEquities from optim.goal??
					level: config.brandEquitiesDef.level, //'product'
					//data: config.brandEquities
					data: Object.keys(config.brandEquities).reduce(
						(accum: any, key: any) => {
							// convert key to level-less format, e.g. 'model:5' to '5'
							const newKey: any = key.replace(config.brandEquitiesDef.level + ":","")
							accum[newKey] = Object.keys(config.brandEquities[key]).reduce(
								(accum2: any, key2: any) => {
									accum2[key2] = config.brandEquities[key][key2]?.play
									return accum2
								},
								{},
							)
							return accum
						},
						{},
					),
				},
				brand_equities_to_optimize: {
					level: optim.config.brand_equities_to_optimize.level,
					perceptions: Object.keys(config.brandEquities).reduce(
						(accum: any, key: any) => {
							// convert key to level-less format, e.g. 'model:5' to '5'
							const newKey: any = key.replace(optim.config.brand_equities_to_optimize.level, + ":","")
							const arr: any = Object.keys(config.brandEquities[key]).reduce(
								(accum2:any, key2:any) => {
									if (config.brandEquities[key][key2].allowed) {
										accum2.push(key2)
									}
									return accum2
								},
								[],
							)
							if (arr.length > 0) accum[newKey] = arr
							return accum
						},
						{},
					),
				},
			},
			assumptions: {
				// this attaches on the backend server i think. don't delete without adjusting backend, because backend assumes it exists.
            },

            //*********THIS IS SET BY 'OPTIMIZERPAYLOAD' IN META**************
            // optimizerSettings: {
            //     algorithm: 'genetic', // Default algorithm
            //     geneticAlgorithm: {
            //         population: 100, // Default population for genetic algorithm
            //         seed: 123456789, // Seed value for randomness
            //         elitism: 5, // Default elitism value
            //         mutationRate: 0.01 // Default mutation rate
            //     }
            // }
            //************* */
		}
		// console.log("optimizer QUERY", query)

		const url: string = `${globals.apiRoot}/optimize/start`
		try {
			const response: any = await apiService.aPost(state.auth, url, query)

			if (response.status === 202) { //server has received the request and accepted it for processing, but the processing has not yet been completed
				const value: any = await response.json()
				console.log("optimization accepted", value)

				// todo: reload the optinfo stuff
				// load the optruns 1 time
				{
					const f = async () => {

						dispatch({ type: OPTIM_SET_RUNLIST_LOADING, value: true })

						//setLoadingItems(true)
						try {
							const response = await apiService.aGet(state.auth, `${globals.apiRoot}/study/${state.study.uid}/optruns`)
							if( response.ok ){
								const runs = await response.json()
								//setItems(runs)
								//console.log('runs', runs)

								dispatch({ type: OPTIM_SET_RUNLIST_DATA, listData: runs })
								dispatch({ type: OPTIM_SET_RUNLIST_LOADING, value: false })
							}
						}
						catch (err) {
							console.error(err)
						}
						
                    }
                    
					f()

				}

				
			} else {
				console.error("Optimization failed to start.", response.statusText)
				// dispatch({
				// 	type: NOTIF_MARK_COMPLETE, // 'COMPLETE_RUNNING_NOTIFICATION',
				// 	runId,
				// 	success: false,
				// 	message: "Optimization failed to start. " + response.statusText,
				// })
			}
		} catch (err) {
			console.error("Optimizer error", err)

			// dispatch({
			// 	type: NOTIF_MARK_COMPLETE, //'COMPLETE_RUNNING_NOTIFICATION',
			// 	runId,
			// 	success: false,
			// 	message: "Optimization run failed: " + JSON.stringify(err),
			// })
		}
	} 

}

export const reducer = (state: OptimState = initialState, action: any): OptimState => {
    

    switch (action.type) {

        case STUDY_RESET: {
            return initialState
        }

        case STUDY_RECEIVE: {
            return {
                ...initialState,
                studyId: action.value.uid,
                uid: action.value.uid, // unique ID for the optimization run
                label: action.value.label, // label for the study
                baseCaseId: action.value.baseCaseId, // Base case ID from the action payload
                config: {
                    ...initialState.config,                    
                    products: action.value.products || [], // Populate the products array from action value or set as empty if not provided
                    equity_drivers: {
                        level: action.value.equityDriversLevel || 'Brand', // From action payload or default to 'Brand'
                        data: action.value.equityDriversData || {} // From action payload or default to empty
                    },
                    brand_equities_to_optimize: {
                        level: action.value.brandEquitiesLevel || 'Brand', // From action payload or default to 'Brand'
                        perceptions: action.value.brandEquitiesPerceptions || {} // From action payload or default to empty
                    },
                },
                goal: {
                    targetMeasure: 'share', // default target measure
                    targetFilter: '1==1', // default filter
                    targetFilterLabel: 'All Products', // default label
                    targetProducts: [], // To be populated later based on product selections
                    targetCosts: {}
                },
                assumptions: action.value.assumptions || {}, // Place assumptions here instead of in config
                
                // optimizerSettings: {
                //     algorithm: 'genetic', // Default algorithm
                //     geneticAlgorithm: {
                //         population: 100, // Default population for genetic algorithm
                //         seed: 123456789, // Seed value for randomness
                //         elitism: 5, // Default elitism value
                //         mutationRate: 0.01 // Default mutation rate
                //     }
                // },
               
                defaultFilterLabel: action.value.defaultFilterLabel || initialState.defaultFilterLabel, // From action payload or fallback to initial state
                optRunList: [], // Default empty list for optimization runs
                optRunDict: {}, // Default empty dictionary for optimization runs
                loading: false, // Reset loading state
                error: null // Clear any previous errors
            }        

            // return {
            //     ...initialState,
            //     prodsToOpt: [],
            //     config: {
            //         ...initialState.config,
            //         assumptionsId: action.value.assumptionsId,
            //         baseCaseId: action.value.baseCaseId,
            //         etag: guid()
            //     },
                
            //     defaultFilterLabel: action.value?.defaultFilterLabel || initialState.defaultFilterLabel,
            //     error: action.error,
            // }           
        }
            
        case OPTIM_SET_METRIC_CONSTRAINTS:
            const {constraints} = action
            return {
                ...state,
                goal: {
                ...state.goal,
                metricConstraints: constraints
                }
            }
            
    
            
        case OPTIM_SET_LEVEL_COSTS: {
            const { costs } = action

            return {
                ...state,
                goal: {
                    ...state.goal,
                    targetCosts: {
                        ...costs
                    }
                }
            }
        }

            
        case OPTIM_INIT_ATTS: {
            const { model } = action

            return {
                ...state,
                config: {
                    ...state.config,
                    products: model?.config?.play.map((p: any) => ({
                        uid: p.uid,
                        label: p.label, // Including the label
                        available: p.play?.available,
                        atts: model?.config?.productDef?.atts.filter((f:any) => !f.readOnly).map((a: any) => ({
                            optimizeable: false,
                            name: a.name,
                            label: a.label,
                            type: a.type,
                            ...(a.type === 'discrete'
                                ? { levels: a.levels.map((lev:any) => ({...lev, allowed: true})) || [] } // Assign levels for discrete attributes
                                : { min: a.min || 0, max: a.max || 100 } // Assign min/max for continuous attributes
                            )
                        }))
                    }))
                }
            }
        }
            
        case OPTIM_ADD_OPTIMIZATION_SELECTION: {
            const { prod } = action

            // Find existing target products in the goal
            const prevTargetProducts: any[] = state?.goal?.targetProducts || []

            // Check if the product is already in the target products list
            const found = prevTargetProducts.find((pl: any) => pl.uid === prod.uid)

            // If the product is not already selected, add it to the target products
            const newTargetProducts = found 
                ? prevTargetProducts 
                : [...prevTargetProducts, { uid: prod.uid, label: prod.label }]

            return {
                ...state,
                goal: {
                    ...state.goal,
                    targetProducts: newTargetProducts 
                }
            }
        }


            
        case OPTIM_REMOVE_OPTIMIZATION_SELECTION: {

            const { prod } = action

            // Remove the product from the targetProducts array in the goal
            const newTargetProducts = state.goal.targetProducts.filter((tp: any) => tp.uid !== prod.uid)

            return {
                ...state,
                goal: {
                    ...state.goal,
                    targetProducts: newTargetProducts // Update targetProducts only
                }
            }
        }     
        

            
            
        case OPTIM_SET_ATT_LOCKED: {
            const { prodUid, optimizeable, attName } = action

            return {
                ...state,
                config: {
                    ...state.config,
                    products: state.config.products.map((prod) => {
                         if (prod.uid !== prodUid) return prod  // Only modify the matching product

                        return {
                            ...prod,
                            atts: prod.atts.map((att) => {
                                if (att.name !== attName || att.type !== 'discrete' || !att.levels) {
                                    return att  // If not the matching attribute, return unchanged
                                }

                                // Modify levels if discrete attribute
                                const updatedLevels = att.levels.map((level) => {
                                    return {
                                        ...level,
                                        allowed: optimizeable // Update the "allowed" flag for the matching level
                                    }
                                })

                                return {
                                    ...att,
                                    levels: updatedLevels
                                }
                            })
                        }
                    })
                }
            }
        }

            
        case OPTIM_SET_LEVEL_ALLOWED: {
            const { prodUid, attName, levelValue, allowed } = action

            return {
                ...state,
                config: {
                    ...state.config,
                    products: state.config.products.map((prod) => {
                        if (prod.uid !== prodUid) return prod  // Only modify the matching product

                        return {
                            ...prod,
                            atts: prod.atts.map((att) => {
                                if (att.name !== attName || att.type !== 'discrete' || !att.levels) {
                                    return att  // If not the matching attribute, return unchanged
                                }

                                // Modify levels if discrete attribute
                                const updatedLevels = att.levels.map((level) => {
                                    if (level.value !== levelValue) return level // Return unchanged level
                                    return {
                                        ...level,
                                        allowed: allowed // Update the "allowed" flag for the matching level
                                    }
                                })

                                return {
                                    ...att,
                                    levels: updatedLevels
                                }
                            })
                        }
                    })
                }
            }
        }
            
        case OPTIM_SET_OPTIMIZATION_METRIC: {


            return {
                ...state,
                goal: {
                    ...state.goal,
                    targetMeasure: action.value,
                },
            }
        }

            
            
        case MAINHUB_MESSAGE_RECEIVED: {
        
            switch (action.payload?.type) {
                case "optrun-status-update": {

                    // todo: should i check user id to isolate only runs that the user cares about?

                    const data = action.payload.data;
                    if (!data) return state;

                    let optRunDict = { ...state.optRunDict };
                    optRunDict[data?.runid] = {
                        ...optRunDict[data?.runid],
                        ...data
                    };
                    optRunDict[data?.runid].hasOutput = optRunDict[data?.runid].output; // update hasOutput hack

                    return {
                        ...state,
                        optRunDict
                    }

                }
            }        
        }        
    
            
            
        case OPTIM_SET_RUNLIST_LOADING: return {
            ...state,
            loading: action.value
        }


        case OPTIM_SET_RUNLIST: return {
            ...state,
            optRunList: action.list
        }


        case OPTIM_SET_RUNLIST_DATA: {

            let dict = {
                ...state.optRunDict
            };
            action.listData.forEach((item: any) => {
                dict[item.id] = {
                    ...dict[item.id],
                    ...item
                };
            })

            return {
                ...state,
                optRunList: action.listData.map((item: any) => item.id),
                optRunDict: dict
            }
        }
            
        case OPTIM_UPDATE_RUNITEM: {
            const updatedRunItem = {
                ...state.optRunDict[action.id],  
                ...action.data,                  
                hasOutput: !!action.data?.output // Ensure boolean value for hasOutput
            }

            return {
                ...state,
                optRunDict: {
                    ...state.optRunDict,
                    [action.id]: updatedRunItem 
                }
            }
        }

            
            
        // case OPTIM_UPDATE_RUNITEM: {
        //     let dict = {
        //         ...state.optRunDict
        //     }

        //     dict[action.id] = {
        //         ...dict[action.id],
        //         ...action.data,
        //     }

        //     dict[action.id].hasOutput = dict[action.id].output; // update hasOutput hack

        //     return {
        //         ...state,
        //         optRunDict: dict
        //     }
        // }

    
            

    // switch (action.type) {
        
    //     case OPTIMIZE_OPTRUN_REQUEST : return {
    //         ...state,
    //         optRuns: {
    //             ...state.optRuns,
    //             [action.runId]: {
    //                 loading: true
    //             }
    //         }
    //     };

    //     case OPTIMIZE_OPTRUN_RECEIVE : return {
    //         ...state,
    //         optRuns: {
    //             ...state.optRuns,
    //             [action.runId]: {
    //                 loading: false,
    //                 error: action.error,
    //                 ...action.value
    //             }
    //         }
    //     };

    //     case OPTIMIZE_OPTRUN_SCENARIO_REQUEST: {
    //         console.log('OPTIMIZE_OPTRUN_SCENARIO_REQUEST', action);
    //         const optRuns = state.optRuns || {};
    //         const optRun: OptRun = optRuns[action.runId];
    //         const output: any = optRun?.output || {};
    //         let scenarios = [...(output?.scenarios || [])];

    //         // add scenario if needed
    //         if(!scenarios.find(s => s.key === action.key) ){
    //             scenarios.push({ key: action.key });
    //         }
            
    //         scenarios = scenarios.map(s => s.key === action.key ? {
    //             ...s,
    //             loading: true
    //         } : s);

    //         return {
    //             ...state,
    //             optRuns: {
    //                 ...state.optRuns,
    //                 [action.runId]: {
    //                     ...optRun,
    //                     output: {
    //                         ...output,
    //                         scenarios
    //                     }
    //                 }
    //             }
    //         };
    //     }

    //     case OPTIMIZE_OPTRUN_SCENARIO_RECEIVE: {
            
    //         console.log('OPTIMIZE_OPTRUN_SCENARIO_RECEIVE', action);
    //         const optRuns = state.optRuns || {};
    //         const optRun:OptRun = optRuns[action.runId];
    //         const output = optRun?.output;
    //         let scenarios = output?.scenarios.map((s: any) => s.key === action.key ? {
    //             ...s,
    //             loading: false,
    //             error: action.error,
    //             config: (action.value || {}).config
    //         } : s);
                        
    //         return {
    //             ...state,
    //             optRuns: {
    //                 ...state.optRuns,
    //                 [action.runId]: {
    //                     ...optRun,
    //                     output: {
    //                         ...output,
    //                         scenarios
    //                     }
    //                 }
    //             }
    //         };
    //     }

    //     // case 'RECEIVE_SCENARIO_LIST': return {
    //     //     ...state,
    //     //     scenarios: [...action.value]
    //     // }

    //     // case 'SET_VALUE': return {
    //     //     ...state,
    //     //     [action.name]: action.value
    //     // };

    //     // case 'SET_VALUES': {
    //     //     let retVal = { ...state };
    //     //     if( action.map ){
    //     //         Object.entries(action.map).forEach(entry =>
    //     //             retVal[entry[0]] = entry[1]
    //     //         )
    //     //     }
    //     //     return retVal;
    //     // };
        
    }

    return state


}
